import { Suspense, useEffect, useState } from "react";

import { ConfigProvider as AntdConfigProvider } from "antd";
import arEG from "antd/locale/ar_EG";
import enGB from "antd/locale/en_GB";
import enUS from "antd/locale/en_US";
import frFR from "antd/locale/fr_FR";
import i18n from "i18next";
import Backend from "i18next-http-backend";
import moment from "moment";
import "moment/dist/locale/ar";
import "moment/dist/locale/en-gb";
import "moment/dist/locale/fr";
import path from "path-browserify";
import { initReactI18next } from "react-i18next";

import { ErrorBoundaryWithoutRouter as ErrorBoundary } from "~/app/components/ErrorBoundary";
import Loader from "~/app/components/Loader";

import { usePublicConfigurationQuery } from "~/app/data/appDataHooks";

import { ModalMessageProvider } from "~/app/contexts/modalMessage";

import { DEFAULT_LANGUAGE_WITHOUT_REGION } from "~/app/constant";
import { BASE_URL } from "~/app/constant/env";

import styleVariables from "~/app/styles/exports.module.scss";

i18n
  .use(Backend)
  // .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    ns: ["translation", "errorSuccessMessages"],
    defaultNS: "translation",
    // debug: IS_DEV,
    fallbackLng: DEFAULT_LANGUAGE_WITHOUT_REGION,
    nsSeparator: "::",
    interpolation: {
      escapeValue: false,
    },
    backend: {
      loadPath: path.join(BASE_URL, "/locales/{{lng}}/{{ns}}.json"),
    },
  });

const getAntdLocale = (languageCode) => {
  switch (languageCode?.toLowerCase()) {
    case "fr":
    case "fr-fr":
      return frFR;
    case "ar":
    case "ar-eg":
      return arEG;
    case "en":
    case "en-gb":
      return enGB;
    case "en-us":
      return enUS;
    default:
      return enGB;
  }
};

window.i18n = i18n;

const ConfigProvider = ({ children }) => {
  const { data: publicConfig } = usePublicConfigurationQuery();

  const [antdLocale, setAntdLocale] = useState(enUS);

  // Set moment, antd and i18n locales based on config language
  useEffect(() => {
    if (publicConfig?.language && publicConfig?.language_without_region) {
      moment.locale(publicConfig.language);
      setAntdLocale(getAntdLocale(publicConfig.language));
      i18n.changeLanguage(publicConfig.language_without_region);
    }
  }, [publicConfig]);

  // Add mac-os class to body if user is using mac
  useEffect(() => {
    const isMacDevice = /Macintosh/.test(navigator.userAgent);
    if (isMacDevice) {
      document.body.classList.add("mac-os");
    }
  }, []);

  return (
    <Suspense fallback={<Loader size="large" type="absolute" />}>
      <AntdConfigProvider
        locale={antdLocale}
        theme={{
          token: {
            colorWhite: styleVariables.white,
            black: styleVariables.black,
            colorPrimary: styleVariables.primaryColor,
            colorLink: styleVariables.linkColor,
            colorLinkHover: styleVariables.linkColorHover,
            colorHighlight: styleVariables.highlightColor,
            colorSuccess: styleVariables.successColor,
            colorInfo: styleVariables.primaryColor,
            colorError: styleVariables.errorColor,
            colorWarning: styleVariables.warningColor,
            colorTextHeading: styleVariables.headingColor,
            colorTextBase: styleVariables.textColor,
            colorText: styleVariables.textColor,
            colorTextSecondary: styleVariables.textColorSecondary,
            colorTextDisabled: styleVariables.disabledColor,
            colorBorder: styleVariables.borderColorBase,
            colorBorderSecondary: styleVariables.borderColorSecondary,
            colorSplit: styleVariables.borderColorSplit,
            fontFamily: styleVariables.fontFamily,
            fontFamilyCode: styleVariables.codeFamily,
            boxShadow: styleVariables.boxShadow,
            borderRadius: parseInt(styleVariables.borderRadius), // Using parseInt because antd need an integer. See https://github.com/ant-design/ant-design/issues/42546#issuecomment-1558866068
          },
          components: {
            Badge: {
              colorError: styleVariables.accentColor,
              colorErrorHover: styleVariables.accentColorHover,
            },
            Select: {
              colorFillSecondary: styleVariables.primaryColorLightened7,
            },
            Tag: {
              defaultBg: styleVariables.primaryColorLightened7,
            },
            Table: {
              bodySortBg: styleVariables.primaryColorLightened9,
              headerBorderRadius: styleVariables.borderRadius,
            },
            Drawer: {
              footerPaddingBlock: 16,
            },
          },
        }}
        direction={
          publicConfig?.language_without_region === "ar" ? "rtl" : "ltr"
        }
      >
        <ModalMessageProvider>
          <ErrorBoundary>{children}</ErrorBoundary>
        </ModalMessageProvider>
      </AntdConfigProvider>
    </Suspense>
  );
};

export default ConfigProvider;
